import * as Yup from 'yup';
import {
  BusinessType,
  FieldData,
  GoogleAddressField,
  Select,
  TextArea,
  TextField,
  Tooltip,
  UploadFile,
  useForm,
} from 'vopay';
import { Toggle } from 'UI';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { buildMatcher, matchers, useValidationMessages } from '@/hooks/useFields/useFields.helpers';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useBusinessTypeOptions, useCountryNames } from '@/hooks';
import { toAlpha2 } from '@/constants/countries';
import useWillSendOnboardingEmail from './useWillSendOnboardingEmail';
import { ParentAccountOption, formatParentAccountLabel } from './parentAccountOptions';
import type { DetailsFormValues } from './types';

/**
 * The cards the step lays its fields out in. `subAccountInfo` is the one card sub-account mode shows
 * in place of the three top-level ones (spec 5.3, Step 1).
 */
export type DetailsSection = 'parentAccount' | 'businessInfo' | 'subAccountInfo' | 'businessAddress' | 'primaryContact';

const INITIAL_VALUES: DetailsFormValues = {
  parentAccountID: '',
  legalBusinessName: '',
  dbaName: '',
  businessType: '',
  country: '',
  industry: '',
  industrySubCategory: '',
  websiteURL: '',
  businessEmailAddress: '',
  businessPhone: '',
  flowOfFundsFile: '',
  flowOfFundsFileName: '',
  address: '',
  unit: '',
  city: '',
  province: '',
  postalCode: '',
  firstName: '',
  lastName: '',
  emailAddress: '',
  businessModel: '',
  numberOfYears: '',
  isMSB: false,
  socialMediaPage: '',
  notes: '',
};

/** A parent picked switches the form to sub-account mode; "None" ('') is the top-level account form. */
export function isSubAccountMode(values: Pick<DetailsFormValues, 'parentAccountID'>): boolean {
  return values.parentAccountID !== '';
}

// The top-level form's fields hide in sub-account mode (their values stay, so switching back brings
// them back as they were) and, hidden, are neither validated nor serialized.
const topLevelOnly = (values: DetailsFormValues) => !isSubAccountMode(values);

function textField(
  fieldName: Extract<keyof DetailsFormValues, string>,
  label: string,
  placeholder: string,
  getInputState: FieldData<DetailsFormValues>['getInputState'],
  validate?: FieldData<DetailsFormValues>['validate'],
  showField: FieldData<DetailsFormValues>['showField'] = () => true,
): FieldData<DetailsFormValues> {
  return {
    fieldName,
    label: () => label,
    getInputState,
    showField,
    validate,
    renderField: ({ getFormikData }) => {
      const fieldData = getFormikData<string>({});

      return (
        <TextField
          variant="outlineShadow"
          placeholder={placeholder}
          {...fieldData}
          value={String(fieldData.value ?? '')}
        />
      );
    },
  };
}

type UseDetailsFormConfigArgs = {
  businessTypes: { [businessTypeKey: string]: BusinessType };
  /** The accounts a sub-account can be created under; the picker lists them after "None". */
  parentAccountOptions: ParentAccountOption[];
  /** Spec 5.1: the picker shows only to an Admin with at least one eligible parent. */
  isParentFieldShown: boolean;
  /** Set when opened from an account's Sub-Accounts tab: the form starts under this parent, read-only. */
  lockedParentAccountID?: string;
  onSubmit: () => void;
};

export default function useDetailsFormConfig({
  businessTypes,
  parentAccountOptions,
  isParentFieldShown,
  lockedParentAccountID,
  onSubmit,
}: UseDetailsFormConfigArgs) {
  const { t } = useTranslation('translations', { keyPrefix: 'PartnerAccountCreation.Details' });
  const validationMessages = useValidationMessages();
  const { canSeeBusinessCase } = useModuleFeatureFlag('accounts');
  const willSendOnboardingEmail = useWillSendOnboardingEmail();
  const { permissions } = usePermissions();

  // Countries the Google autocomplete offers when no Country is selected yet, per the partner's
  // add-account permissions (the Country select itself stays unrestricted, matching the old form).
  const permittedGoogleCountries =
    permissions.CanAddCanadianAccounts && permissions.CanAddUSAccounts
      ? ['CA', 'US']
      : permissions.CanAddUSAccounts
        ? ['US']
        : permissions.CanAddCanadianAccounts
          ? ['CA']
          : undefined;

  // Once a Country is selected, the autocomplete restricts to it. The form stores alpha3; Google
  // takes alpha2, which countries.ts maps to via toAlpha2.
  const googleCountriesFor = (selectedAlpha3: string): string[] | undefined => {
    const selected = toAlpha2(selectedAlpha3);

    return selected ? [selected] : permittedGoogleCountries;
  };

  const businessTypeOptions = useBusinessTypeOptions();
  const { countryOptions } = useCountryNames();

  const businessTypeSelectOptions = useMemo(
    () => Object.entries(businessTypeOptions).map(([value, label]) => ({ value, label })),
    [businessTypeOptions],
  );

  const countrySelectOptions = useMemo(
    () => Object.entries(countryOptions).map(([value, label]) => ({ value, label })),
    [countryOptions],
  );

  const industrySelectOptions = useMemo(
    () =>
      Object.values(businessTypes)
        // The backend requires BusinessTypeID and BusinessTypeCategoryID as a pair, so an industry
        // with no categories can never be submitted — offering it would dead-end the form on the
        // then-required (but empty) sub-category select.
        .filter((businessType) => Object.keys(businessType.Categories ?? {}).length > 0)
        .map((businessType) => ({
          value: businessType.BusinessTypeID,
          label: businessType.Industry,
        })),
    [businessTypes],
  );

  const noParentLabel = t('noParent', { defaultValue: 'None (top-level account)' });

  // "None" first and selected by default; then every eligible parent as its path · id (spec 5.2).
  // The picker filters on that text, so typing either the name or the id finds an account.
  const parentSelectOptions = useMemo(
    () => [
      { value: '', label: noParentLabel },
      ...parentAccountOptions.map((option) => ({ value: option.accountID, label: formatParentAccountLabel(option) })),
    ],
    [parentAccountOptions, noParentLabel],
  );

  const enterPlaceholder = t('enterPlaceholder', { defaultValue: 'Enter' });
  const selectPlaceholder = t('selectPlaceholder', { defaultValue: 'Select' });

  const businessCaseOnly = (values: DetailsFormValues) => canSeeBusinessCase && topLevelOnly(values);

  // The backend requires a business phone for a US account, and a sub-account's country is its
  // parent's — so the phone is asked for under a US parent and nowhere else in sub-account mode.
  const hasUSParent = (values: DetailsFormValues) =>
    parentAccountOptions.find((option) => option.accountID === values.parentAccountID)?.country === 'US';

  const topLevelOrUSParent = (values: DetailsFormValues) => topLevelOnly(values) || hasUSParent(values);

  const config: FieldData<DetailsFormValues>[] = [
    // --- Parent Account (ID-13797) ---
    {
      fieldName: 'parentAccountID',
      label: () => t('parentAccount', { defaultValue: 'Parent Account' }),
      // No help text under a locked parent: there is nothing to select.
      helpText: () =>
        lockedParentAccountID
          ? undefined
          : t('parentAccountHelp', {
              defaultValue: "Select a parent to create a sub-account. Sub-accounts inherit the parent's settings.",
            }),
      // From the Sub-Accounts tab the parent is the account being viewed and cannot be changed, and
      // the picker looks it: disabled, not merely read-only (which styled like an editable field).
      // A disabled field is skipped by validation and serialization; the value stays in ctx.values.
      getInputState: () => (lockedParentAccountID ? 'disabled' : 'optional'),
      showField: () => isParentFieldShown,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<DetailsFormValues['parentAccountID']>({});

        return (
          <Select
            version="2"
            filterable
            sort={false}
            options={parentSelectOptions}
            // '' is "None": the trigger then shows the placeholder, which is that option's own label.
            placeholder={noParentLabel}
            emptyFilterMsg={t('parentAccountNoMatch', { defaultValue: 'No account matches your search.' })}
            {...fieldData}
          />
        );
      },
    },
    // --- Business Information ---
    textField(
      'legalBusinessName',
      t('legalName', { defaultValue: 'Legal Business Name' }),
      enterPlaceholder,
      () => 'required',
      () =>
        Yup.string()
          .matches(
            new RegExp(buildMatcher([matchers.alpha, matchers.numeric, matchers.whitespace, matchers.hyphen]), 'i'),
            validationMessages.noSpecialChars,
          )
          .max(99)
          .required(t('legalNameRequired', { defaultValue: 'Please enter a legal business name.' })),
    ),
    textField(
      'dbaName',
      t('dba', { defaultValue: 'Doing Business As (DBA Name)' }),
      enterPlaceholder,
      () => 'optional',
      () =>
        Yup.string()
          .max(254)
          .matches(
            new RegExp(buildMatcher([matchers.alpha, matchers.numeric, matchers.whitespace, matchers.hyphen]), 'i'),
            validationMessages.noSpecialChars,
          ),
    ),
    {
      fieldName: 'businessType',
      label: () => t('businessType', { defaultValue: 'Business Type' }),
      getInputState: () => 'required',
      showField: topLevelOnly,
      validate: () =>
        Yup.string().required(t('businessTypeRequired', { defaultValue: 'Please select a business type.' })),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<DetailsFormValues['businessType']>({});

        return (
          <Select
            version="2"
            options={businessTypeSelectOptions}
            placeholder={selectPlaceholder}
            sort={false}
            {...fieldData}
          />
        );
      },
    },
    {
      fieldName: 'country',
      label: () => t('country', { defaultValue: 'Country' }),
      getInputState: () => 'required',
      showField: topLevelOnly,
      validate: () => Yup.string().required(t('countryRequired', { defaultValue: 'Please select a country.' })),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<DetailsFormValues['country']>({});

        return (
          <Select
            version="2"
            options={countrySelectOptions}
            placeholder={selectPlaceholder}
            sort={false}
            {...fieldData}
          />
        );
      },
    },
    {
      fieldName: 'industry',
      label: () => t('industry', { defaultValue: 'Industry' }),
      getInputState: () => 'required',
      showField: topLevelOnly,
      validate: () => Yup.string().required(t('industryRequired', { defaultValue: 'Please select an industry.' })),
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<DetailsFormValues['industry']>({
          // The sub-category options belong to the selected industry, so a change invalidates it.
          onChange: () => ctx.setFieldValue('industrySubCategory', ''),
        });

        return (
          <Select
            version="2"
            options={industrySelectOptions}
            placeholder={selectPlaceholder}
            sort={false}
            {...fieldData}
          />
        );
      },
    },
    {
      fieldName: 'industrySubCategory',
      label: () => t('industrySubCategory', { defaultValue: 'Industry Sub-Category' }),
      // The backend requires BusinessTypeID and BusinessTypeCategoryID as a pair — sending an
      // industry without a sub-category is rejected with "Invalid Business Category".
      getInputState: (ctx) => (ctx.values.industry ? 'required' : 'optional'),
      showField: topLevelOnly,
      validate: (ctx) =>
        ctx.values.industry
          ? Yup.string().required(
              t('industrySubCategoryRequired', { defaultValue: 'Please select an industry sub-category.' }),
            )
          : Yup.string(),
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<DetailsFormValues['industrySubCategory']>({});

        const selectedIndustry = Object.values(businessTypes).find(
          (businessType) => businessType.BusinessTypeID === ctx.values.industry,
        );

        const subCategoryOptions = Object.values(selectedIndustry?.Categories ?? {}).map((category) => ({
          value: category.BusinessTypeCategoryID,
          label: category.Category,
        }));

        return (
          <Select
            version="2"
            options={subCategoryOptions}
            placeholder={selectPlaceholder}
            sort={false}
            {...fieldData}
            disabled={!ctx.values.industry || fieldData.disabled}
          />
        );
      },
    },
    textField(
      'websiteURL',
      t('website', { defaultValue: 'Company Website' }),
      'https://',
      () => 'required',
      () =>
        Yup.string()
          .matches(/^[^*|<>[\]{}`]+$/, validationMessages.noSpecialChars)
          .required(t('websiteRequired', { defaultValue: 'Please enter the business website URL.' })),
      topLevelOnly,
    ),
    textField(
      'businessEmailAddress',
      t('businessEmail', { defaultValue: 'Business Email' }),
      'info@company.com',
      () => 'required',
      () => Yup.string().email(validationMessages.invalidEmail).required(validationMessages.missingEmail),
      topLevelOnly,
    ),
    textField(
      'businessPhone',
      t('phone', { defaultValue: 'Business Phone Number' }),
      '6041111111',
      () => 'required',
      () =>
        Yup.string()
          .matches(new RegExp(buildMatcher([matchers.numeric]), 'i'), validationMessages.numbers)
          .test(
            'len',
            t('phoneLength', { defaultValue: 'Phone number must be between 6 and 11 characters' }),
            (value) => (value || '').length >= 6 && (value || '').length <= 11,
          )
          .required(t('phoneRequired', { defaultValue: 'Please enter the business phone number.' })),
      topLevelOrUSParent,
    ),
    {
      fieldName: 'flowOfFundsFile',
      label: () => t('flowOfFunds', { defaultValue: 'Flow of Funds Diagram' }),
      tooltip: () => (
        <Tooltip
          information={t('flowOfFundsHint', {
            defaultValue:
              'Upload a diagram showing who initiates payments, how funds enter and leave the VoPay account, and who the end recipients are.',
          })}
          type="hover"
          variant="outlined"
          icon="info"
          position="top"
          buttonWidth="18px"
          iconWidth="12"
          hasArrow
        />
      ),
      getInputState: () => (canSeeBusinessCase ? 'required' : 'optional'),
      showField: topLevelOnly,
      validate: () =>
        canSeeBusinessCase
          ? Yup.string().required(t('flowOfFundsRequired', { defaultValue: 'A flow of funds diagram is required.' }))
          : Yup.string(),
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<DetailsFormValues['flowOfFundsFile']>({});

        return (
          <UploadFile
            id="flowOfFundsFile"
            name="flowOfFundsFile"
            base64
            keepDataUriPrefix
            keepExtension
            maxMBSize={4}
            value={fieldData.value}
            valueFileName={ctx.values.flowOfFundsFileName}
            deleteButton
            error={fieldData.errorMsg}
            onBlur={fieldData.onBlur}
            onChange={() => {}}
            handleNewFile={(file: string) => {
              ctx.setFieldValue('flowOfFundsFile', file);
              ctx.setFieldTouched('flowOfFundsFile', true);
            }}
            handleFileName={(name: string) => {
              ctx.setFieldValue('flowOfFundsFileName', name);
            }}
          />
        );
      },
    },
    // --- Business Address ---
    {
      fieldName: 'address',
      label: () => t('address', { defaultValue: 'Address' }),
      getInputState: () => 'required',
      showField: topLevelOnly,
      validate: () =>
        Yup.string()
          .required(t('addressRequired', { defaultValue: 'Please enter an address.' }))
          .test(
            'valid-address-values',
            validationMessages.noSpecialChars,
            (value) => !/[^\w ,:'()/\-.]|\s{5}/.test(value || ''),
          ),
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<DetailsFormValues['address']>({});

        return (
          <GoogleAddressField
            variant="outlineShadow"
            placeholder={enterPlaceholder}
            {...fieldData}
            value={String(fieldData.value ?? '')}
            ISOCountry={googleCountriesFor(ctx.values.country)}
            onGoogleAddressSelect={(addressComponents) => {
              ctx.setFieldValue('address', addressComponents.address);
              ctx.setFieldValue('city', addressComponents.city);
              ctx.setFieldValue('province', addressComponents.province);
              ctx.setFieldValue('postalCode', addressComponents.postalcode);
            }}
          />
        );
      },
    },
    textField(
      'unit',
      t('unit', { defaultValue: 'Unit' }),
      enterPlaceholder,
      () => 'optional',
      () =>
        Yup.string().test(
          'valid-address-values',
          validationMessages.noSpecialChars,
          (value) => !/[^\w ,:'()/\-.]|\s{5}/.test(value || ''),
        ),
      topLevelOnly,
    ),
    textField(
      'city',
      t('city', { defaultValue: 'City' }),
      enterPlaceholder,
      () => 'required',
      () => Yup.string().required(t('cityRequired', { defaultValue: 'Please enter a city.' })),
      topLevelOnly,
    ),
    textField(
      'province',
      t('provinceState', { defaultValue: 'Province/State' }),
      enterPlaceholder,
      () => 'required',
      () => Yup.string().required(t('provinceStateRequired', { defaultValue: 'Please enter a province or state.' })),
      topLevelOnly,
    ),
    textField(
      'postalCode',
      t('postalZip', { defaultValue: 'Postal Code/Zip Code' }),
      enterPlaceholder,
      () => 'required',
      () =>
        Yup.string()
          .min(3)
          .max(10)
          .required(t('postalCodeRequired', { defaultValue: 'Please enter a postal code.' })),
      topLevelOnly,
    ),
    // --- Primary Contact Information ---
    textField(
      'firstName',
      t('firstName', { defaultValue: 'First Name' }),
      enterPlaceholder,
      () => 'required',
      () =>
        Yup.string()
          .max(49)
          .required(t('firstNameRequired', { defaultValue: 'Please enter a first name.' })),
      topLevelOnly,
    ),
    textField(
      'lastName',
      t('lastName', { defaultValue: 'Last Name' }),
      enterPlaceholder,
      () => 'required',
      () =>
        Yup.string()
          .max(49)
          .required(t('lastNameRequired', { defaultValue: 'Please enter a last name.' })),
      topLevelOnly,
    ),
    {
      // Shared by both modes: the primary contact's email for a top-level account, the sub-account's
      // login / welcome email in sub-account mode.
      ...textField(
        'emailAddress',
        t('email', { defaultValue: 'Email Address' }),
        'name@domain.com',
        () => 'required',
        () => Yup.string().email(validationMessages.invalidEmail).required(validationMessages.missingEmail),
      ),
      // Promising the onboarding email only makes sense for partners it will actually send for, and
      // only for a top-level account — a sub-account gets the welcome email, not the application form.
      tooltip: (ctx) =>
        willSendOnboardingEmail && topLevelOnly(ctx.values) ? (
          <Tooltip
            information={t('emailHint', {
              defaultValue: 'The onboarding application form will be sent to this email address.',
            })}
            type="hover"
            variant="outlined"
            icon="info"
            position="top"
            buttonWidth="18px"
            iconWidth="12"
            hasArrow
          />
        ) : undefined,
    },
    // --- Business Information, continued: only rendered when the partner requires a business
    // case, so each of these gates itself rather than relying on a section-level condition. ---
    textField(
      'businessModel',
      t('businessModel', { defaultValue: 'Business Model' }),
      enterPlaceholder,
      () => 'required',
      () =>
        Yup.string()
          .matches(/^[^*|<>[\]{}`]+$/, validationMessages.noSpecialChars)
          .max(999)
          .required(t('businessModelRequired', { defaultValue: 'Please describe the business model.' })),
      businessCaseOnly,
    ),
    textField(
      'numberOfYears',
      t('numberOfYears', { defaultValue: 'Number of Years in Business' }),
      '1',
      () => 'required',
      () =>
        Yup.number()
          .transform((_, value: string) => parseFloat(String(value).replace(/,/g, '')))
          .min(1, validationMessages.minOne)
          .required(t('numberOfYearsRequired', { defaultValue: 'Please enter the number of years in business.' })),
      businessCaseOnly,
    ),
    textField(
      'socialMediaPage',
      t('socialMedia', { defaultValue: 'Business Social Media' }),
      'https://',
      () => 'required',
      () => Yup.string().required(t('socialMediaRequired', { defaultValue: 'Please enter a social media page.' })),
      businessCaseOnly,
    ),
    {
      fieldName: 'isMSB',
      label: () => t('isMSB', { defaultValue: 'Is this a money service business (MSB)?' }),
      getInputState: () => 'optional',
      showField: businessCaseOnly,
      renderField: ({ ctx }) => (
        <Toggle
          name="isMSB"
          checked={ctx.values.isMSB}
          onChange={(event) => ctx.setFieldValue('isMSB', event.target.checked)}
        />
      ),
    },
    {
      fieldName: 'notes',
      label: () => t('notes', { defaultValue: 'Notes' }),
      getInputState: () => 'optional',
      // Old-form parity: Notes always showed for partners (business-case only gated requiredness
      // of other fields), and the business-case call always carries it.
      showField: topLevelOnly,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<DetailsFormValues['notes']>({});

        return (
          <TextArea
            variant="outlineShadow"
            placeholder={t('notesPlaceholder', { defaultValue: 'Enter any additional notes...' })}
            {...fieldData}
            value={String(fieldData.value ?? '')}
          />
        );
      },
    },
  ];

  const byName = (fieldNames: string[]) => config.filter(({ fieldName }) => fieldNames.includes(fieldName));

  const sections: Record<DetailsSection, FieldData<DetailsFormValues>[]> = {
    parentAccount: byName(['parentAccountID']),
    businessInfo: byName([
      'legalBusinessName',
      'dbaName',
      'businessType',
      'country',
      'industry',
      'industrySubCategory',
      'websiteURL',
      'businessEmailAddress',
      'businessPhone',
      'notes',
      'flowOfFundsFile',
      'businessModel',
      'numberOfYears',
      'socialMediaPage',
      'isMSB',
    ]),
    subAccountInfo: byName(['legalBusinessName', 'dbaName', 'emailAddress', 'businessPhone']),
    businessAddress: byName(['address', 'unit', 'city', 'province', 'postalCode']),
    primaryContact: byName(['firstName', 'lastName', 'emailAddress']),
  };

  // useForm marks every field before the focused one touched, by config position — so it must get
  // the on-screen order, or focusing a Business Info field flags the untouched address/contact ones.
  // Memoized: Formik only reads initialValues once, but a fresh object per render would still be
  // compared by the form layer on every pass.
  const initialValues = useMemo(
    () => ({ ...INITIAL_VALUES, parentAccountID: lockedParentAccountID ?? '' }),
    [lockedParentAccountID],
  );

  const { ctx, renderFormElements } = useForm<DetailsFormValues>({
    initialValues,
    config: [
      ...sections.parentAccount,
      ...sections.businessInfo,
      ...sections.businessAddress,
      ...sections.primaryContact,
    ],
    onSubmit,
  });

  return {
    ctx,
    renderSection: (section: DetailsSection) => renderFormElements({ config: sections[section] }),
  };
}
